import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

interface PaginationProps {
  total: number
  limit: number
  offset: number
  onOffsetChange: (offset: number) => void
  onLimitChange: (limit: number) => void
  pageSizeOptions?: number[]
}

export function Pagination({
  total,
  limit,
  offset,
  onOffsetChange,
  onLimitChange,
  pageSizeOptions = [10, 20, 50, 100],
}: PaginationProps) {
  const currentPage = Math.floor(offset / limit) + 1
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + limit, total)

  const goToPage = (page: number) => {
    const clamped = Math.max(1, Math.min(page, totalPages))
    onOffsetChange((clamped - 1) * limit)
  }

  // Build the page number list with ellipsis
  const buildPageList = (): (number | '...')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages: (number | '...')[] = [1]
    if (currentPage > 3) pages.push('...')
    for (
      let p = Math.max(2, currentPage - 1);
      p <= Math.min(totalPages - 1, currentPage + 1);
      p++
    ) {
      pages.push(p)
    }
    if (currentPage < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  const pageList = buildPageList()

  return (
    <div className="flex items-center justify-between px-1 py-2 select-none">
      {/* Left – rows per page */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span className="hidden sm:inline font-medium">Rows per page</span>
        <div className="relative">
          <select
            value={limit}
            onChange={(e) => {
              onLimitChange(Number(e.target.value))
              onOffsetChange(0)
            }}
            className="appearance-none pl-3 pr-7 py-1.5 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 cursor-pointer hover:border-gray-300 transition-colors"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <ChevronRight
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rotate-90 h-3.5 w-3.5 text-gray-400"
          />
        </div>
        <span className="text-gray-400 hidden md:inline">
          {total === 0 ? '0 result' : `${from}–${to} of ${total}`}
        </span>
      </div>

      {/* Right – page navigation */}
      <div className="flex items-center gap-1">
        {/* First */}
        <button
          onClick={() => goToPage(1)}
          disabled={currentPage === 1}
          title="First page"
          className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>

        {/* Prev */}
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          title="Previous page"
          className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-0.5">
          {pageList.map((page, idx) =>
            page === '...' ? (
              <span
                key={`ellipsis-${idx}`}
                className="w-8 text-center text-gray-400 text-sm"
              >
                …
              </span>
            ) : (
              <button
                key={page}
                onClick={() => goToPage(page as number)}
                className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                  page === currentPage
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            )
          )}
        </div>

        {/* Next */}
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          title="Next page"
          className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        {/* Last */}
        <button
          onClick={() => goToPage(totalPages)}
          disabled={currentPage === totalPages}
          title="Last page"
          className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
